/**
 * Driftwood stack (`hush.driftwood.v1`) — weathered planks leaning against an unseen wall to the right, with one or
 * two round logs lying in front.
 *
 * Frame 100×80, support at the bottom-center (50, 80). Planks are silver-brown (wood mixed toward stone), with a
 * salt-bleached left edge, a shaded right edge and long grain lines; logs show pale end grain with concentric
 * rings (rings, never knots or holes: no face-like marks). A rope tie binds the planks for most seeds.
 * `count` (3..6) is the total number of pieces (5+ adds a second log); `lean` (0.2..1) tips the planks from near
 * upright toward ~40°. No definitions, no collision or gameplay geometry. Deterministic for the recipe.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface DriftwoodParams {
  readonly count: number; // integer 3..6
  readonly lean: number;  // 0.2..1
}
export type DriftwoodRecipe = PieceRecipe<'hush.driftwood.v1', DriftwoodParams>;

export const DRIFTWOOD_FRAME = { w: 100, h: 80 } as const;

export const HUSH_DRIFTWOOD_STACK: DriftwoodRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.driftwood.stack.v1', generatorId: 'hush.driftwood.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { count: 5, lean: 0.5 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi)) throw new Error(`hush.driftwood.v1: ${k} must be ${lo}..${hi}`);
  return v;
}

const W = DRIFTWOOD_FRAME.w, H = DRIFTWOOD_FRAME.h;
const GROUND = H - 0.9;

export function buildDriftwood(recipe: DriftwoodRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.driftwood.v1');
  checkScope(scope);
  const count = num(recipe.params, 'count', 3, 6);
  if (!Number.isInteger(count)) throw new Error('hush.driftwood.v1: count must be an integer');
  const lean = num(recipe.params, 'lean', 0.2, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;
  const logs = count >= 5 ? 2 : 1;
  const planks = count - logs;

  // ---- planks: back (tallest) to front; tops rest near the wall on the right ----
  let plankMarkup = '';
  const spans: { y: number; x0: number; x1: number }[] = [];
  const ropeY = 38 + rnd('rope.y') * 10;
  for (let i = 0; i < planks; i++) {
    const k = `plank.${i}`;
    const theta = ((6 + 30 * lean) + (rnd(`${k}.a`) - 0.5) * 8) * Math.PI / 180;
    let len = (72 - i * 7) * (0.9 + rnd(`${k}.len`) * 0.15);
    const topX = 94 - i * 10 - rnd(`${k}.tx`) * 5;
    len = Math.min(len, (topX - 6) / Math.sin(theta), (GROUND - 3) / Math.cos(theta));
    const w = 8 + rnd(`${k}.w`) * 4;
    const dir = { x: Math.sin(theta), y: -Math.cos(theta) };
    const nrm = { x: Math.cos(theta), y: Math.sin(theta) };
    const bx = topX - len * dir.x;
    const at = (t: number, s: number): Point => ({ x: bx + dir.x * len * t + nrm.x * s * w / 2, y: GROUND + dir.y * len * t + nrm.y * s * w / 2 });
    // Ends: flat-ish cut at the ground, a broken ragged top.
    const rag = rnd(`${k}.rag`) * 2.5;
    const outlinePts: Point[] = [
      { x: bx - w / 2 / Math.cos(theta), y: GROUND }, { x: bx + w / 2 / Math.cos(theta), y: GROUND },
      at(0.55, 1.06), at(1 - rag / len, 1), at(1, 0.2), at(1 - (rag + 1.5) / len, -0.3), at(1 - 0.4 / len, -1), at(0.45, -0.95),
    ];
    const fill = mix(roles.woodLight, roles.stoneLight, 0.25 + rnd(`${k}.tone`) * 0.35);
    let p = el('path', { d: poly(outlinePts), fill, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
    p += el('path', { d: poly([at(0.04, 0.55), at(0.96, 0.55), at(0.96, 0.95), at(0.04, 0.95)]), fill: roles.woodShadow, opacity: 0.32 });
    p += el('path', { d: poly([at(0.05, -0.62), at(0.92, -0.62)], false), stroke: roles.salt, 'stroke-width': 1.1, opacity: 0.9 });
    let grain = '';
    for (let g = 0; g < 2; g++) {
      const s = -0.15 + g * 0.4 + (rnd(`${k}.g${g}`) - 0.5) * 0.15;
      const a = at(0.08 + rnd(`${k}.g${g}.a`) * 0.2, s), b = at(0.6 + rnd(`${k}.g${g}.b`) * 0.3, s + 0.12), m = at(0.4, s - 0.15);
      grain += `M${n(a.x)} ${n(a.y)}Q${n(m.x)} ${n(m.y)} ${n(b.x)} ${n(b.y)}`;
    }
    p += el('path', { d: grain, fill: 'none', stroke: roles.outlineSoft, 'stroke-width': 0.6, opacity: 0.7 });
    plankMarkup += p;
    // Record where this plank crosses the rope height (for the tie).
    const t = (GROUND - ropeY) / (len * Math.cos(theta));
    if (t > 0.1 && t < 0.92) {
      const c = at(t, 0);
      spans.push({ y: ropeY, x0: c.x - w / 2 - 0.5, x1: c.x + w / 2 + 0.5 });
    }
  }

  // ---- rope tie across the planks it reaches ----
  let rope = '';
  if (spans.length >= 2 && rnd('rope.on') < 0.75) {
    const x0 = Math.min(...spans.map(s => s.x0)), x1 = Math.max(...spans.map(s => s.x1));
    const slope = -0.12;
    const band = (dy: number) => `M${n(x0)} ${n(ropeY + dy)}L${n(x1)} ${n(ropeY + dy + (x1 - x0) * slope)}`;
    rope = el('path', { d: band(0), stroke: outline, 'stroke-width': 3.6, 'stroke-linecap': 'round' })
      + el('path', { d: band(0), stroke: roles.paperShadow, 'stroke-width': 2.2, 'stroke-linecap': 'round' })
      + el('path', { d: `M${n(x0 + 2)} ${n(ropeY + 0.4)}l-2.4 7M${n(x0 + 3.2)} ${n(ropeY + 0.4)}l0.6 6.2`,
        stroke: roles.paperShadow, 'stroke-width': 1.2, 'stroke-linecap': 'round' });
  }

  // ---- logs: capsules lying along the ground, end grain on the left end ----
  let logMarkup = '';
  for (let i = 0; i < logs; i++) {
    const k = `log.${i}`;
    const r = (i === 0 ? 7 : 5.5) + rnd(`${k}.r`) * 1.5;
    const len = (i === 0 ? 48 : 34) + rnd(`${k}.len`) * 12;
    const x0 = i === 0 ? 50 - len * (0.45 + rnd(`${k}.x`) * 0.25) : 8 + rnd(`${k}.x`) * 10;
    const cy = i === 0 ? GROUND - r - 0.3 : GROUND - 2 * 7 - r * 0.6;
    const x1 = Math.min(W - r - 2, x0 + len);
    const d = `M${n(x0)} ${n(cy - r)}H${n(x1)}A${n(r * 0.55)} ${n(r)} 0 0 1 ${n(x1)} ${n(cy + r)}H${n(x0)}Z`;
    const barkFill = mix(roles.woodBase, roles.stoneBase, 0.35 + rnd(`${k}.tone`) * 0.2);
    let lg = el('path', { d, fill: barkFill, stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round' });
    lg += el('path', { d: `M${n(x0 + 3)} ${n(cy - r * 0.55)}H${n(x1 - 1)}`, stroke: roles.salt, 'stroke-width': 1.2, opacity: 0.75 });
    lg += el('path', { d: `M${n(x0 + 3)} ${n(cy + r * 0.5)}H${n(x1 - 2)}`, stroke: roles.woodShadow, 'stroke-width': 1.6, opacity: 0.5 });
    const crack = `M${n(x0 + len * 0.3)} ${n(cy - 1)}q${n(len * 0.12)} -1.4 ${n(len * 0.28)} 0.4`;
    lg += el('path', { d: crack, fill: 'none', stroke: roles.outlineSoft, 'stroke-width': 0.7 });
    if (i > 0) { // only the front log shows end grain: two ringed ends side by side would read as eyes
      lg += el('path', { d: `M${n(x0)} ${n(cy - r)}A${n(r * 0.55)} ${n(r)} 0 0 0 ${n(x0)} ${n(cy + r)}`, fill: barkFill, stroke: outline, 'stroke-width': 1.2 });
      logMarkup = lg + logMarkup;
      continue;
    }
    // End grain: pale ellipse with two rings and a radial check — no central dot.
    lg += el('ellipse', { cx: x0, cy, rx: r * 0.6, ry: r, fill: mix(roles.salt, roles.woodLight, 0.35), stroke: outline, 'stroke-width': 1.1 });
    lg += el('ellipse', { cx: x0, cy, rx: r * 0.4, ry: r * 0.66, fill: 'none', stroke: roles.woodLight, 'stroke-width': 0.8 });
    lg += el('ellipse', { cx: x0, cy, rx: r * 0.2, ry: r * 0.33, fill: 'none', stroke: roles.woodLight, 'stroke-width': 0.7 });
    lg += el('path', { d: `M${n(x0)} ${n(cy - r * 0.1)}L${n(x0 - r * 0.45)} ${n(cy - r * 0.8)}`, stroke: roles.outlineSoft, 'stroke-width': 0.7 });
    logMarkup = lg + logMarkup; // the second log sits behind the first
  }

  const shadow = el('ellipse', { cx: 55, cy: H - 2.2, rx: 42, ry: 1.8, fill: outline, opacity: 0.2 });
  const body = partGroup(parts, 'driftwood-shadow', shadow)
    + partGroup(parts, 'driftwood-planks', plankMarkup + rope)
    + partGroup(parts, 'driftwood-logs', logMarkup);
  return { frame: { ...DRIFTWOOD_FRAME }, body, anchors: { support: { x: W / 2, y: H } }, parts };
}
